import React, { useState, useEffect, useRef } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBook, faCamera, faMicrophone, faFlag, faGift, faScroll } from '@fortawesome/free-solid-svg-icons';
import API from '../../../api/axiosInstance';

const ATHLETE_ITEMS = [
  { key: 'yardbookHandedOver',        label: 'Yardbook handed over',          icon: faBook       },
  { key: 'headshotTaken',             label: 'Headshot taken',                icon: faCamera     },
  { key: 'audioPronunciationCreated', label: 'Audio pronunciation created',   icon: faMicrophone },
  { key: 'flagSignatureMade',         label: 'Flag signature made',           icon: faFlag       },
  { key: 'giftHandedOver',            label: 'Gift handed over',              icon: faGift       },
];

const CADDIE_ITEMS = [
  { key: 'yardbookHandedOver',      label: 'Yardbook handed over',       icon: faBook   },
  { key: 'regulationsAcknowledged', label: 'I have read the Regulations', icon: faScroll },
];

const emptyChecked = (items) => Object.fromEntries(items.map(i => [i.key, false]));

const OnsiteChecklistModal = ({ show, onHide, participant, type, onChecklistUpdated }) => {
  const items = type === 'caddie' ? CADDIE_ITEMS : ATHLETE_ITEMS;

  const [checked,  setChecked]  = useState(() => emptyChecked(items));
  const [loading,  setLoading]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  const prevCheckedRef = useRef(null);

  useEffect(() => {
    if (!show || !participant?._id) return;
    setError('');
    setLoading(true);
    API.get(`/ems/participants/${participant._id}`)
      .then(res => {
        const saved = res.data?.person_data?.onsiteChecklist;
        const base  = emptyChecked(items);
        const next  = saved && typeof saved === 'object'
          ? Object.fromEntries(Object.keys(base).map(k => [k, !!saved[k]]))
          : base;
        setChecked(next);
        prevCheckedRef.current = next;
      })
      .catch(() => setError('Failed to load checklist.'))
      .finally(() => setLoading(false));
  }, [show, participant?._id]);

  const toggle = async (key) => {
    const prev = checked;
    const next = { ...checked, [key]: !checked[key] };
    setChecked(next);
    setSaving(true);
    setError('');
    try {
      await API.patch(`/ems/onsite/participants/${participant._id}/checklist`, {
        onsiteChecklist: next,
      });
      prevCheckedRef.current = next;
      onChecklistUpdated?.(participant._id, next);
    } catch {
      setChecked(prev);
      setError('Failed to save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const firstName = participant?.person_data?.givenFirstName  || participant?.person_id?.name  || '';
  const lastName  = participant?.person_data?.familyLastName  || participant?.person_id?.lname || '';
  const name      = [firstName, lastName].filter(Boolean).join(' ') || 'Participant';

  const doneCount = Object.values(checked).filter(Boolean).length;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton style={{ borderBottom: '1px solid #f0e4c8', background: '#FFFBF4' }}>
        <Modal.Title style={{ fontSize: 16, fontWeight: 700, color: '#5c4518' }}>
          Checklist — {name}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ background: '#FFFBF4', padding: '20px 24px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <Spinner animation="border" size="sm" style={{ color: '#d9a128' }} />
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <span style={{ fontSize: 12, color: '#a0895a' }}>
                {doneCount} / {items.length} completed
              </span>
              {saving && <Spinner animation="border" size="sm" style={{ color: '#d9a128' }} />}
            </div>
            {error && (
              <div style={{ fontSize: 12, color: '#c0392b', marginBottom: 10 }}>{error}</div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {items.map(item => (
                <label
                  key={item.key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    cursor: saving ? 'not-allowed' : 'pointer',
                    padding: '10px 14px',
                    borderRadius: 8,
                    background: checked[item.key] ? '#fdf3dc' : '#fff',
                    border: `1px solid ${checked[item.key] ? '#d9a128' : '#e8dcc8'}`,
                    transition: 'background 0.15s, border-color 0.15s',
                    opacity: saving ? 0.7 : 1,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked[item.key]}
                    onChange={() => !saving && toggle(item.key)}
                    style={{ accentColor: '#d9a128', width: 16, height: 16, cursor: saving ? 'not-allowed' : 'pointer' }}
                  />
                  <FontAwesomeIcon
                    icon={item.icon}
                    style={{ color: checked[item.key] ? '#d9a128' : '#a0895a', fontSize: 14, flexShrink: 0 }}
                  />
                  <span style={{
                    fontSize: 14,
                    color: checked[item.key] ? '#5c4518' : '#6b5d48',
                    fontWeight: checked[item.key] ? 600 : 400,
                  }}>
                    {item.label}
                  </span>
                  {checked[item.key] && (
                    <i className="fa-solid fa-check" style={{ marginLeft: 'auto', color: '#d9a128', fontSize: 12 }} />
                  )}
                </label>
              ))}
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer style={{ borderTop: '1px solid #f0e4c8', background: '#FFFBF4', justifyContent: 'flex-end' }}>
        <button
          onClick={onHide}
          style={{
            padding: '6px 20px',
            fontSize: 13,
            fontWeight: 600,
            background: 'transparent',
            border: '1px solid #c49220',
            borderRadius: 6,
            color: '#c49220',
            cursor: 'pointer',
          }}
        >
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default OnsiteChecklistModal;
